/**
 * Was Vespera getan hat — in einer Zeile statt in einer Karte. (06.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Außerdem wird im Chatverlauf sehr viel Technisches angezeigt,
 * was ich für den User, glaub ich, nicht brauche. Und auch ich brauch es
 * nicht. Vielleicht kann man es so vereinfachen, dass es nicht zu viel
 * Information ist, sondern eher nur kompakt … mit weniger Cards, eher
 * zusammengeführt, dass man auch die Prozesse nicht in tausend Einzelteile
 * zerlegt, dass das Ganze flüssiger und leichter zum Lesen wird."*
 *
 * Gemessen am 06.09. an zwei Tagen echter Züge: ein einzelner Zug erzeugte
 * bis zu **32 Karten** hintereinander, 42 der 72 Werkzeugläufe der Woche
 * waren `shell_exec` — Nachsehen im Protokoll, im Code, in der Datenbank.
 * Jede dieser Karten trug einen Rahmen, einen Titel und einen Ausschnitt
 * der Ausgabe. Zwischen zwei Sätzen Antwort lagen so drei Bildschirme
 * Technik.
 *
 * ## Die Trennung
 *
 * Ein Werkzeug liefert entweder **Inhalt** (das Wetter, ein Bild, ein Lied,
 * eine Verbindung, eine E-Mail) — das bleibt eine Karte, denn es ist die
 * Antwort selbst. Oder es ist ein **Schritt** auf dem Weg dorthin
 * (nachgesehen, gesucht, gelesen) — das ist Arbeit, kein Ergebnis, und
 * gehört in eine Zeile.
 *
 * ⚠️ Die Politik (seit 15.10.2026 im Register) ist eine Positivliste: Ein Werkzeug ohne Zeile ist
 *    im Zweifel Inhalt und bekommt seine Karte. Lieber eine Karte zu viel
 *    als ein Ergebnis, das in einer zugeklappten Zeile verschwindet.
 */

import { ART_KLASSE, gruppenKopf, setzeGruppenZahl, weitereHinweis } from "./karte.js";
import { STAND_WARTET, wartetAufJa } from "./karten-text.js";
import { werkzeugName } from "./werkzeug-namen.js";
import { menschlicherFehler } from "./fehler-worte.js";
import { politikVon, werkzeugeMitPolitik } from "../../shared/karten-politik.js";
import { leseAnzeige, STAND_WORT } from "../../shared/karten-anzeige.js";

/**
 * Werkzeuge, deren Lauf ein Schritt ist und kein Ergebnis.
 *
 * ⚠️ Seit 15.10.2026 (Plan „Karten zu 100 % einheitlich“) KEINE Liste mehr hier: die Politik steht je Werkzeug im
 *    Register (server/werkzeug-register-daten.ts, Feld `karte`), abgeleitet nach shared/karten-politik-daten.ts. Die
 *    bisherige Positivliste (Nachsehen, Gedächtnis, Projektdateien, Browser, Rechnungspositionen, read_url,
 *    werkzeug_pruefen, Webseiten-Stand …) ist dort die Erstbelegung — tests/server/karten-politik.test.ts hält fest,
 *    dass keines davon zur Karte wurde. Ein Werkzeug, das das Register nicht kennt, ist weiter im Zweifel Inhalt.
 */
export const SCHRITT_WERKZEUGE: ReadonlySet<string> = new Set(werkzeugeMitPolitik("schritt"));

export function istSchritt(tool: string): boolean {
  return politikVon(tool) === "schritt";
}

/** Die leise Zeile für ein Werkzeug ohne eigene Karte und ohne Anzeige (eine alte gespeicherte Karte): Name · Stand. */
export function leiseZeile(tool: string, isError: boolean): { verb: string; gegenstand: string; fehler: boolean } {
  return { verb: werkzeugName(tool), gegenstand: isError ? STAND_WORT.fehler : STAND_WORT.ok, fehler: isError };
}

/** Das Verb — was getan wurde, nicht wie es heisst. */
const VERBEN: Readonly<Record<string, string>> = {
  shell_exec: "Nachgesehen",
  changelog: "Änderungen nachgelesen",
  messwerte: "Messwerte gelesen",
  gedaechtnis_selbsttest: "Gedächtnis geprüft",
  current_time: "Zeit geprüft",
  user_location_get: "Standort geprüft",
  memory_search: "Im Gedächtnis gesucht",
  memory_save: "Gemerkt",
  memory_delete: "Notiz gelöscht",
  stammdaten_merken: "Stammdatum gemerkt",
  stammdaten_vergessen: "Stammdatum gelöscht",
  antwortmodus_setzen: "Antwortweise umgestellt",
  aussprache_merken: "Aussprache gemerkt",
  vorschlag_verwerfen: "Vorschlag verworfen",
  schluessel_ablegen: "Schlüssel abgelegt",
  stimme_benennen: "Stimme gemerkt",
  telegram_nachricht: "Auf Telegram geschickt",
  ticket_list: "Aufgaben durchgesehen",
  ticket_list_open: "Offene Aufgaben durchgesehen",
  ticket_roadmap: "Fahrplan durchgesehen",
  ticket_link: "Aufgaben verknüpft",
  thread_list: "Gespräche durchgesehen",
  alltag_search: "Im Alltag gesucht",
  project_file_list: "Projektdateien durchgesehen",
  project_file_get: "Projektdatei gelesen",
  project_file_create: "Projektdatei angelegt",
  project_file_update: "Projektdatei geändert",
  project_file_delete: "Projektdatei gelöscht",
  project_link: "Projekt verknüpft",
  project_create: "Projekt angelegt",
  project_set_active: "Projekt gewechselt",
  read_file: "Datei gelesen",
  browser_open_url: "Seite geöffnet",
  browser_get_active_tab: "Im Browser nachgesehen",
  browser_get_page_text: "Seite gelesen",
  browser_click: "Geklickt",
  browser_type: "Eingetippt",
  browser_scroll: "Gescrollt",
  browser_list_tabs: "Tabs durchgesehen",
  browser_switch_tab: "Tab gewechselt",
  browser_close_tab: "Tab geschlossen",
  browser_form_submit: "Formular abgeschickt",
  browser_hover: "Mauszeiger darüber",
  browser_drag: "Gezogen",
  browser_upload: "Datei abgelegt",
  faktura_invoice_item_add: "Position hinzugefügt",
  faktura_invoice_item_update: "Position geändert",
  faktura_invoice_item_delete: "Position gelöscht",
  faktura_invoice_items_list: "Positionen durchgesehen",
  faktura_articles: "Artikel nachgeschlagen",
  faktura_customers: "Kunden nachgeschlagen",
  faktura_customer_get: "Kundendaten gelesen",
};

/**
 * Was in der Zeile hinter dem Verb steht — fuer Werkzeuge, deren Ergebnis ein
 * SATZ ist und kein Datensatz. (15.09.2026)
 *
 * Ohne das griffe die allgemeine Regel weiter unten auf das Feld `text` und
 * setzte den ganzen Satz in die Zeile:
 *   „Position geändert — Position 5167 geändert: Fahrtkostenzuschuss
 *    (200.00× €0.50 = €120.00 brutto). Neue Summe: netto €1050.00 …"
 * Das Verb stuende dann doppelt da, und die laufende Summe dreimal
 * untereinander — genau das, worueber Martin sich beschwert hat.
 *
 * ⚠️ Greift ein Muster nicht, faellt die Zeile auf den gekuerzten Satz
 *    zurueck. Sie wird dann laenger, aber nie leer oder falsch. Die Muster
 *    sind in tests/frontend/schritte-faktura.test.ts an die ECHTE Ausgabe
 *    der Werkzeuge gebunden — aendert sich der Wortlaut, faellt es dort auf.
 */
const SATZ_MUSTER: Readonly<Record<string, RegExp>> = {
  // „Position 5167 geändert: Fahrtkostenzuschuss (200.00× €0.50 = …"
  faktura_invoice_item_update: /^Position\s+(\d+)\s+geändert:\s*(.+?)\s*\(/u,
  // „Position hinzugefügt zu Rechnung 8999 — neue item_id 912"
  faktura_invoice_item_add: /neue item_id\s+(\d+)[\s\S]*?\n\s*\d+:\s*([^\n—]+?)\s*—/u,
  // „Position 5167 aus Rechnung 8999 gelöscht. Neue Summe: …"
  faktura_invoice_item_delete: /^Position\s+(\d+)\s+aus Rechnung\s+(\d+)\s+gelöscht/u,
};

/** Die Zeile fuer ein Werkzeug, das einen Satz liefert — oder null. */
function satzGegenstand(tool: string, text: string): string | null {
  const muster = SATZ_MUSTER[tool];
  if (!muster) return null;
  const t = muster.exec(text.trim());
  if (!t) return null;
  const teile = t.slice(1).filter((x): x is string => typeof x === "string" && x.trim() !== "");
  if (teile.length === 0) return null;
  // Erst der Name, dann die Kennung — der Name traegt die Bedeutung.
  if (teile.length >= 2) return `${teile[1].trim()} · Nr. ${teile[0].trim()}`;
  return teile[0].trim();
}

/** Felder, aus denen sich der Gegenstand des Schritts ablesen laesst. */
const GEGENSTAND_FELDER = [
  "command",
  "query",
  "q",
  "suchbegriff",
  "path",
  "pfad",
  "file",
  "name",
  "title",
  "titel",
  "url",
  "text",
  "content",
];

export function auspacken(result: unknown): Record<string, unknown> {
  let r = result;
  if (typeof r === "string") {
    try {
      r = JSON.parse(r) as unknown;
    } catch {
      return { text: result };
    }
  }
  if (!r || typeof r !== "object") return {};
  const o = r as Record<string, unknown>;
  // Manche Werkzeuge verpacken ihr Ergebnis noch einmal.
  const innen = o.result ?? o.data;
  if (innen && typeof innen === "object" && !Array.isArray(innen)) {
    return { ...(innen as Record<string, unknown>), ...o };
  }
  return o;
}

function kuerzen(s: string, max: number): string {
  const eine = s.replace(/\s+/g, " ").trim();
  return eine.length <= max ? eine : eine.slice(0, max - 1).trimEnd() + "…";
}

export type SchrittZeile = { verb: string; gegenstand: string; fehler: boolean; wartet?: true };

/**
 * Eine Zeile fuer einen Schritt: was getan wurde, und woran.
 *
 * Der Gegenstand bleibt kurz — er soll erkennen lassen, worum es ging, nicht
 * den Befehl nachvollziehbar machen. Wer das braucht, liest das Protokoll.
 *
 * ## Seit 15.10.2026: der Gegenstand kommt aus der Anzeige des Servers
 *
 * Trägt das Ereignis eine `anzeige` (server/werkzeug-anzeige.ts, aus
 * strukturierten Feldern gebaut), liest die Zeile NUR sie: das Verb bleibt
 * das hiesige (oder der Titel der Anzeige), der Gegenstand ist ihre Zeile,
 * der Stand ihr Stand. Das Rohergebnis wird dann nicht angefasst — kein
 * `command`, kein `query`, kein `text` mehr in der Zeile. Faltung und
 * Zählung (gruppenTitel, data-fehler, data-wartet) bleiben, wie sie sind.
 *
 * ⚠️ Ohne Anzeige (eine vor dem Umbau gespeicherte Karte) gilt der alte Weg
 *    über die Felder — er stirbt, sobald kein Verlauf mehr ohne Anzeige ist.
 */
export function schrittZeile(tool: string, result: unknown, isError = false, anzeige?: unknown): SchrittZeile {
  const a = leseAnzeige(anzeige);
  const verb = VERBEN[tool] ?? a?.titel ?? werkzeugName(tool);
  if (a) {
    if (a.stand === "wartet") return { verb, gegenstand: STAND_WARTET, fehler: false, wartet: true };
    if (a.stand === "fehler" || isError) return { verb, gegenstand: a.zeile ?? "", fehler: true };
    return { verb, gegenstand: a.zeile ?? "", fehler: false };
  }
  const r = auspacken(result);
  /*
   * 15.10.2026 (Martin: „Blender · Speichern · 2 Schritte · 2 gescheitert“
   * über einem gelungenen Speichern): ein Halt des Ja-Tors ist kein
   * Fehlschlag — das Werkzeug hat nichts getan und wartet auf Martins Ja.
   * Die Zeile sagt das leise; der Kopf zählt es nicht als gescheitert.
   */
  if (wartetAufJa(r)) return { verb, gegenstand: STAND_WARTET, fehler: false, wartet: true };
  const fehler =
    isError === true ||
    r.ok === false ||
    (typeof r.error === "string" && r.error.length > 0);
  if (fehler) {
    // Der Satz fuer Menschen, nicht der Schluessel (dieselbe Uebersetzung wie
    // auf den Karten). Ohne Satz bleibt der Grund, sonst nichts.
    const grund =
      menschlicherFehler(r, "") ||
      (typeof r.reason === "string" && r.reason) ||
      "";
    return { verb, gegenstand: grund ? kuerzen(grund, 110) : "", fehler: true };
  }
  // 15.09.2026: Werkzeuge, deren Ergebnis ein Satz ist, tragen ihren
  // Gegenstand IM Satz — sonst stuende er noch einmal ganz in der Zeile.
  if (typeof r.text === "string") {
    const kurz = satzGegenstand(tool, r.text);
    if (kurz) return { verb, gegenstand: kuerzen(kurz, 72), fehler: false };
  }
  for (const feld of GEGENSTAND_FELDER) {
    const v = r[feld];
    if (typeof v === "string" && v.trim().length > 0) {
      return { verb, gegenstand: kuerzen(v, 72), fehler: false };
    }
  }
  // Kein Gegenstand, aber vielleicht eine Menge — „7 gefunden".
  for (const feld of ["count", "anzahl", "total", "treffer"]) {
    const v = r[feld];
    if (typeof v === "number" && Number.isFinite(v)) {
      return { verb, gegenstand: `${v} gefunden`, fehler: false };
    }
  }
  for (const v of Object.values(r)) {
    if (Array.isArray(v) && v.length > 0) {
      return { verb, gegenstand: `${v.length} gefunden`, fehler: false };
    }
  }
  return { verb, gegenstand: "", fehler: false };
}

/**
 * Die Zusammenfassung ueber der zugeklappten Gruppe.
 *
 * Ein Schritt nennt sich selbst. Mehrere gleiche nennen ihr Verb und ihre
 * Zahl. Gemischte nennen das haeufigste Verb mit „u. a." — sonst behauptet
 * die Zeile etwas Falsches: „Gemerkt · 32 Schritte" liest sich, als waeren
 * 32 Dinge gemerkt worden, obwohl 30 davon Nachsehen waren. (06.09.2026,
 * am Bildschirm gesehen.)
 */
export function gruppenTitel(verben: readonly string[], gescheitert = 0): string {
  // 21.09.2026 (#2418): 68 gescheiterte Schritte in 30 Tagen lagen zugeklappt
  // und unsichtbar. Die Zahl steht jetzt im Kopf — und die Gruppe oeffnet sich.
  const schwanz = gescheitert > 0 ? ` · ${gescheitert} gescheitert` : "";
  if (verben.length === 0) return `Schritte${schwanz}`;
  if (verben.length === 1) return `${verben[0]}${schwanz}`;
  const zaehler = new Map<string, number>();
  for (const v of verben) zaehler.set(v, (zaehler.get(v) ?? 0) + 1);
  let haeufigstes = verben[0];
  for (const [v, n] of zaehler) {
    if (n > (zaehler.get(haeufigstes) ?? 0)) haeufigstes = v;
  }
  const kopf = zaehler.size === 1 ? haeufigstes : `${haeufigstes} u. a.`;
  return `${kopf} · ${verben.length} Schritte${schwanz}`;
}

/**
 * Die Gruppe finden, in die dieser Schritt gehoert. (07.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit Bild: *„warum schon wieder seriell?!"* — viermal „Gemerkt"
 * untereinander, jedes in einer eigenen zugeklappten Gruppe. Beim Neuladen
 * derselben Stelle stand dagegen sauber „Gemerkt · 4 Schritte".
 *
 * Vorher musste die Gruppe UNMITTELBAR vor der Einfuegestelle stehen. Live
 * schiebt sich zwischen zwei Schritte aber staendig etwas Fluechtiges: die
 * Fortschritts-Blase eines Werkzeugs, der „arbeitet"-Platzhalter, eine
 * Skelett-Kachel. Lag so etwas dahinter, machte der naechste Schritt eine
 * neue Gruppe auf — und die Teilung blieb, bis die Seite neu lud.
 *
 * ## Die Regel
 *
 * Eine Gruppe gehoert zu EINEM Zug (einer Frage und allem, was Vespera
 * darauf tut). Kennt der Aufrufer den Zug, entscheidet allein die Kennung —
 * unabhaengig davon, was inzwischen dazwischenliegt. Ein neuer Zug bekommt
 * zwingend eine neue Gruppe; das war der Sinn der alten Regel und bleibt.
 *
 * ⚠️ Ohne Zug (Karte von einem anderen Geraet, Sprach-Weg) gilt weiter der
 *    unmittelbare Vorgaenger innerhalb von drei Minuten.
 *
 * ## 23.09.2026 — gesucht wird der Behälter des Zuges (Ticket 2418)
 *
 * Martin: *„Auf eine Frage soll eine Card kommen und in der sieht man dann
 * abgearbeitete Prozesse als schöne Gruppierung mit einem Endergebnis."*
 * Die Schritt-Gruppe stand bisher allein, jede Inhaltskarte daneben — bis
 * zu 7 Elemente auf eine Frage. Jetzt gehören beide in EINEN Behälter
 * (`.chat-zug`), und diese Funktion findet ihn nach genau derselben Regel.
 * Die Schritte bleiben darin die zugeklappte `details.chat-schritte`.
 */
export const ZUG_FENSTER_MS = 180_000;

/** Der Behälter eines Zuges — Schritte und Inhaltskarten in EINEM Element. */
const ZUG_KLASSE = "chat-zug";

/**
 * Die Klasse der Blasen, die nur waehrend der Arbeit dastehen.
 *
 * Sie werden gebaut, sobald etwas laeuft, und entfernt, sobald es fertig
 * ist — sie gehoeren keinem Zug und trennen darum auch keine.
 */
const FLUECHTIG = "is-pending";

export function findeZugGruppe(
  container: Element,
  zug: number | null,
  ts: number,
): HTMLElement | null {
  if (zug !== null) {
    const alle = container.querySelectorAll(`.${ZUG_KLASSE}`);
    for (let i = alle.length - 1; i >= 0; i--) {
      const g = alle[i] as HTMLElement;
      if (g.dataset.zug === String(zug)) return g;
    }
    return null;
  }
  let vorgaenger: HTMLElement | null = null;
  for (const kind of Array.from(container.children) as HTMLElement[]) {
    const kts = Number(kind.dataset.ts ?? 0);
    if (!kts) continue;
    if (kts > ts) break;
    /*
     * ⚠️ 09.09.2026 — FLUECHTIGES ZAEHLT NICHT ALS VORGAENGER.
     *
     * Der „arbeitet"-Puls wird nach JEDEM Werkzeug-Ergebnis neu eingehaengt
     * und bekommt dabei einen Zeitstempel. Damit lag zwischen je zwei
     * Schritten garantiert ein Fremdkoerper, und diese Regel machte
     * pflichtschuldig eine neue Gruppe auf — fuenf Werkzeuge, fuenf
     * Anzeigen. Kein Zufall, kein Zeitfenster: es passierte bei jedem Zug
     * mit mehr als einem Werkzeug.
     *
     * Eine Blase, die gleich wieder verschwindet, ist kein Beleg dafuer,
     * dass ein neuer Zug begonnen hat. Sie wird uebersprungen.
     */
    if (kind.classList.contains(FLUECHTIG)) continue;
    vorgaenger = kind;
  }
  if (!vorgaenger?.classList.contains(ZUG_KLASSE)) return null;
  const abstand = Math.abs(ts - Number(vorgaenger.dataset.ts ?? 0));
  return abstand < ZUG_FENSTER_MS ? vorgaenger : null;
}

/**
 * Einen leeren Behälter für einen Zug bauen. (23.09.2026, Ticket 2418)
 *
 * Einordnen (Zeitstempel, Tagestrenner) tut der Aufrufer — das kann nur
 * app.ts mit insertOrdered.
 */
export function legeZugAn(zug: number | null): HTMLElement {
  const behaelter = document.createElement("div");
  behaelter.className = ZUG_KLASSE;
  if (zug !== null) behaelter.dataset.zug = String(zug);
  return behaelter;
}

/** Die Faltung gleicher Werkzeuge eines Zuges. */
export const FALTE_KLASSE = "zug-falte";

/**
 * So viele Karten zeigt eine Faltung offen — die LETZTEN. (15.10.2026)
 *
 * Martin, mit Bild: „Alltag-Wortlaut · 49-mal“, darunter 49 Karten
 * untereinander. Der Kopf nennt die Zahl; offen stehen die neuesten drei,
 * die übrigen holt „und N weitere“ (ein Knopf) auf Tipp.
 */
export const FALTE_OFFEN_MAX = 3;

/** Die Karten einer Faltung — alles unter dem Kopf, was eine Karte ist. */
function faltenKarten(falte: HTMLElement): HTMLElement[] {
  return Array.from(falte.children).filter(
    (k): k is HTMLElement => k instanceof HTMLElement && k.hasAttribute("data-werkzeug"),
  );
}

/** Ist die Faltung aufgeklappt (Martin hat „und N weitere“ getippt)? */
const FALTE_OFFEN = "offen";

/**
 * Nur die letzten FALTE_OFFEN_MAX Karten bleiben sichtbar, davor steht der
 * Knopf „und N weitere“. Wird bei JEDEM Anhängen neu gerechnet — kein
 * mitgeführter Zähler. Eine aufgeklappte Faltung bleibt aufgeklappt.
 */
function falteKuerzen(falte: HTMLElement): void {
  const karten = faltenKarten(falte);
  falte.querySelector(":scope > .tool-card-weitere")?.remove();
  const offen = falte.dataset.falte === FALTE_OFFEN;
  const versteckt = offen ? 0 : Math.max(0, karten.length - FALTE_OFFEN_MAX);
  karten.forEach((k, i) => {
    k.hidden = i < versteckt;
  });
  if (versteckt === 0) return;
  const kopf = falte.querySelector<HTMLElement>(":scope > .zug-falte-kopf");
  const knopf = weitereHinweis(falte, versteckt, () => {
    falte.dataset.falte = FALTE_OFFEN;
    falteKuerzen(falte);
    // Der Knopf ist weg — der Fokus wandert auf die erste nun sichtbare Karte, nicht ins Leere.
    const erste = faltenKarten(falte)[0];
    if (erste) {
      erste.tabIndex = -1;
      erste.focus();
    }
  });
  // Der Knopf steht zwischen Kopf und Karten: „und 46 weitere“, dann die letzten drei.
  if (knopf) (kopf ?? falte.firstElementChild)?.after(knopf);
}

/**
 * Das Stück einer Karte, das nur sagt, WAS sie ist („Web-Suche · “).
 * (23.09.2026, Ticket 2418) — in einer Faltung sagt das der Kopf. Seit
 * 14.10.2026 wohnt es im Grundgerüst (lib/karte.ts); wer es hier holt, bekommt es weiter.
 */
export { ART_KLASSE } from "./karte.js";

/**
 * Eine Inhaltskarte in den Behälter ihres Zuges hängen. (23.09.2026, Ticket 2418)
 *
 * Martin, 20.09.: *„Ich habe dir eine Frage gestellt und dann kamen 10 oder
 * mehr Cards … Auf eine Frage soll eine Card kommen."* Nachgezählt (seit
 * 21.09. 13:37, 88 Antworten): 152 sichtbare Elemente, 38 Antworten mit
 * mehr als einem — darunter 5× Websuche, 3× Aufgabe angelegt, 3× Artikel
 * angelegt, jede als eigene Karte.
 *
 * ## Die Regel
 *
 * - Die Karte kommt IN den Behälter, unter die Schritte, vor dessen
 *   Uhrzeit — der Zug bleibt EIN Element im Verlauf.
 * - Hat derselbe Zug dieses Werkzeug schon gezeigt, wird gefaltet: EIN Kopf
 *   („Websuche · 5-mal"), darunter die Karten als Liste. Die Faltung steht
 *   dort, wo das Werkzeug zuerst erschien.
 * - Tragen alle gefalteten Karten denselben Titel („Aufgabe"), sagt ihn der
 *   Kopf einmal und die Karten schweigen. Verschiedene Titel („Gmail · 3
 *   Treffer", „Gmail · 5 Treffer") bleiben stehen — sie sagen etwas.
 *
 * ⚠️ Die Faltung zählt und prüft bei JEDEM Anhängen neu, was im DOM steht.
 *    Kein mitgeführter Zähler — sonst gäbe es zwei Besitzer derselben Zahl.
 */
export function haengeKarteAn(behaelter: HTMLElement, karte: HTMLElement, werkzeug: string): void {
  karte.dataset.werkzeug = werkzeug;
  // Die Uhrzeit (von insertOrdered) bleibt das letzte Kind des Behälters.
  const uhrzeit = behaelter.querySelector(":scope > .bubble-ts");
  const schon = Array.from(behaelter.querySelectorAll<HTMLElement>(":scope > [data-werkzeug]")).find(
    (k) => k !== karte && k.dataset.werkzeug === werkzeug,
  );
  /*
   * 25.09.2026 (Ticket 2608): Ein Vorschlag, auf den im selben Zug das
   * Ergebnis folgt, ist erledigt — das Ergebnis tritt an seine Stelle.
   * Martin sah „Musik · 2-mal": oben „Noch nicht erzeugt — wartet auf deine
   * Bestätigung", darunter das fertige Lied. Gefaltet hätten beide recht,
   * nebeneinander widersprechen sie sich.
   */
  // 07.10.2026: Eine Absage ist kein Ergebnis — sie nimmt dem Liedvorschlag (Text und Ja) nicht den Platz.
  if (schon && karte.dataset.vorschlag !== "ja" && !karte.classList.contains("tool-error")) {
    const vorschlag = [schon, ...Array.from(schon.querySelectorAll<HTMLElement>(":scope > [data-werkzeug]"))].find(
      (k) => k.dataset.vorschlag === "ja",
    );
    if (vorschlag) {
      vorschlag.replaceWith(karte);
      const falte = karte.parentElement;
      if (falte?.classList.contains(FALTE_KLASSE)) {
        const rest = faltenKarten(falte);
        const kopf = falte.querySelector<HTMLElement>(":scope > .zug-falte-kopf");
        if (rest.length === 1) {
          karte.hidden = false;
          falte.replaceWith(karte);
        } else if (kopf) {
          setzeGruppenZahl(kopf, werkzeugName(werkzeug), rest.length);
          falteKuerzen(falte);
        }
      }
      return;
    }
  }
  if (!schon) {
    behaelter.insertBefore(karte, uhrzeit);
    return;
  }
  let falte = schon;
  if (!schon.classList.contains(FALTE_KLASSE)) {
    falte = document.createElement("div");
    falte.className = FALTE_KLASSE;
    falte.dataset.werkzeug = werkzeug;
    // Der Kopf aus dem Grundgerüst (lib/karte.ts) — kein eigener Stil.
    falte.appendChild(gruppenKopf(werkzeugName(werkzeug), 0));
    behaelter.replaceChild(falte, schon);
    falte.appendChild(schon);
  }
  falte.appendChild(karte);
  const karten = faltenKarten(falte);
  const kopf = falte.querySelector<HTMLElement>(":scope > .zug-falte-kopf");
  if (kopf) setzeGruppenZahl(kopf, werkzeugName(werkzeug), karten.length);
  const titel = karten.map((k) => k.querySelector<HTMLElement>(":scope > .tool-card-title"));
  const ersterTitel = titel[0]?.textContent ?? null;
  const gleich = titel.every((t) => t !== null && t.textContent === ersterTitel);
  for (const t of titel) if (t) t.hidden = gleich;
  // 23.09.2026 (#2418): die Art einer Karte („Web-Suche · …“) nennt der Kopf
  // schon — unter „Websuche · 5-mal“ stand sie sonst fünfmal, in einer
  // zweiten Schreibweise. In der Faltung schweigt sie.
  for (const art of falte.querySelectorAll<HTMLElement>(`.${ART_KLASSE}`)) art.hidden = true;
  // 15.10.2026: nie 49 Karten untereinander — die letzten drei, der Rest auf Tipp.
  falteKuerzen(falte);
}

/**
 * Die gescheiterten Schritte eines Werkzeugs sind überholt, sobald dasselbe
 * Werkzeug im selben Zug gelungen ist. (15.10.2026)
 *
 * Martin, mit Bild: „Blender · Speichern · 2 Schritte · 2 gescheitert“ über
 * einer Karte, die das gelungene Speichern zeigte. Der Gesamtstand zählt;
 * die Vorversuche bleiben als Zeilen, aber leise: `data-fehler="überholt"`
 * statt „ja“ — der Kopf zählt sie nicht mehr, und die Gruppe darf wieder zu.
 * `karte` ist die eben eingehängte Inhaltskarte; gesucht wird in ihrem Zug.
 * Gibt die Schritt-Gruppe zurück, wenn sich etwas geändert hat (dann zieht
 * app.ts ihren Kopf nach) — sonst null.
 */
export function ueberholeSchritte(karte: Element, werkzeug: string): HTMLElement | null {
  const gruppe = karte.closest(`.${ZUG_KLASSE}`)?.querySelector<HTMLElement>(":scope > .chat-schritte") ?? null;
  if (!gruppe) return null;
  let geaendert = false;
  for (const z of Array.from(gruppe.querySelectorAll<HTMLElement>('.chat-schritt[data-fehler="ja"]'))) {
    if (z.dataset.werkzeug !== werkzeug) continue;
    z.dataset.fehler = "überholt";
    geaendert = true;
  }
  return geaendert ? gruppe : null;
}

/**
 * Alle Karten eines Werkzeugs aus dem Verlauf nehmen — samt der Hülle, die
 * sie leer zurückließen. (23.09.2026, Ticket 2418)
 *
 * Die ÖBB-Karte ersetzt sich selbst: eine neue Verbindung räumt jede alte
 * weg (app.ts, entferneOebbKarten). Bisher hieß das „jede `.tool-oebb`
 * entfernen“. Seit der Faltung hängt die Karte aber in einem Behälter und
 * womöglich in einer Faltung. Nahm man nur die Karten, blieb die Faltung
 * stehen — Kopf „Zugverbindung · 2-mal“ über nichts, und weil sie selbst
 * `data-werkzeug` trägt, behielt der Behälter seinen Kartenrahmen. Das
 * geschieht, wenn ein nachgeladener Zug zwei Abfragen hatte (gezählt: 1
 * von Martins 33 Antworten mit ÖBB, 28.08.) und danach live eine neue kommt.
 *
 * Entfernt wird darum die Einheit, die haengeKarteAn baut: je Zug genau EIN
 * Kind mit diesem Werkzeug, die Karte oder ihre Faltung. Bleibt der
 * Behälter danach ohne Inhalt, geht er mit — sonst stünde eine leere Zeile
 * (ein Abstand) im Verlauf, wo vorher gar nichts stand.
 */
export function entferneWerkzeugKarten(wurzel: Element, werkzeug: string): void {
  const teile = wurzel.querySelectorAll<HTMLElement>(`.${ZUG_KLASSE} > [data-werkzeug]`);
  for (const teil of Array.from(teile)) {
    if (teil.dataset.werkzeug !== werkzeug) continue;
    const behaelter = teil.parentElement;
    teil.remove();
    if (behaelter && !behaelter.querySelector(":scope > [data-werkzeug], :scope > .chat-schritte")) {
      behaelter.remove();
    }
  }
}
